// Monatsabschluss (Etappe 5) — der Blick der Personalabteilung auf einen Monat:
// je Mitarbeiter Ist/Soll/Saldo, Zuschlagsstunden, Urlaubs- und Kranktage,
// Hinweise nach dem Arbeitszeitgesetz, Export für die Lohnabrechnung und die Sperre.
//
// Ist ein Monat abgeschlossen, lehnt die Datenbank jede Änderung an Zeiten und
// Abwesenheiten dieses Monats ab — auch von Personal/Geschäftsführung. Wer korrigieren
// muss, hebt den Abschluss zuerst auf; das bleibt sichtbar.
//
// Darstellung im Widget-Stil (design_handoff_personal §6); die Pille behält ihre Steuerung,
// die Steuerzeile der Seite nutzt dieselben Handler.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import {
  CalendarBlank, ChartPieSlice, CircleNotch, DownloadSimple, FileCsv, FirstAid, ListBullets, ListNumbers, LockKey, LockSimpleOpen,
  Scales, Timer, Warning,
} from '@phosphor-icons/react';
import { KennzahlWidget } from '../../../components/ui/KennzahlWidget';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { MonatSchritt, PersonalKopf, SteuerKnopf } from '../personal/teile/Rahmen';
import { AuszahlDialog, LohnartenWidget, MonatKarte } from '../personal/teile/MonatTeile';
import {
  abschlussAufheben, auszahlungErfassen, ladeAbschluesse, ladeAbwesenheiten, ladeAuszahlungen,
  ladeMitarbeiter, ladeModelle, ladeStempel, ladeZuordnungen, monatAbschliessen,
  type Auszahlung, type Monatsabschluss,
} from '../../../lib/zeiterfassung/api';
import { gesamtsaldo, monatsbilanz, stundenText, tagVon, type Arbeitszeitmodell, type Stempel, type Zuordnung } from '../../../lib/zeiterfassung/berechnung';
import {
  auslastung, csvZeile, krankheitsquote, pruefeArbeitszeitgesetz, zuschlaegeSumme, type Verstoss,
} from '../../../lib/zeiterfassung/zuschlaege';
import { aufteilung } from '../../../lib/zeiterfassung/ticketzeit';
import {
  ladeDatevEinstellungen, ladeLohnarten, ladePersonalnummern, ladeTicketZeitenZeitraum,
  lohnartSpeichern, setzeDatevEinstellungen, ladeVerguetung,
} from '../../../lib/zeiterfassung/api';
import { bruttoVorschau, euroText } from '../../../lib/zeiterfassung/verguetung';
import { baueLodasExport, dezimalStunden, type Lohnart } from '../../../lib/zeiterfassung/lohnexport';
import { TYP_INFO, urlaubstageImAntrag, type Abwesenheit } from '../../../lib/zeiterfassung/abwesenheit';
import { sollMinutenAmTag } from '../../../lib/zeiterfassung/berechnung';
import { darfPersonalVerwalten } from '../../../lib/personal/rechte';

interface Props {
  userProfile?: { id?: string; role?: string } | null;
}

interface Zeile {
  id: string;
  name: string;
  ist: number;
  soll: number;
  saldo: number;
  nacht: number;
  sonntag: number;
  feiertag: number;
  urlaubstage: number;
  kranktage: number;
  verstoesse: Verstoss[];
  abschluss: Monatsabschluss | null;
  /** Stundenkonto insgesamt (nach Auszahlungen) — Grundlage fürs Auszahlen */
  konto: number;
  auszahlungen: Auszahlung[];
  /** Soll-Arbeitstage des Monats — Nenner der Krankheitsquote */
  sollTage: number;
  /** Abrechenbar auf Tickets gebuchte Minuten */
  abrechenbar: number;
  /** Ist-Stunden × Stundenlohn — null, wenn kein Satz hinterlegt ist. */
  brutto: number | null;
}

const monatText = (m: string) => {
  const [j, mm] = m.split('-').map(Number);
  return new Date(j, (mm || 1) - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};
const verschieben = (m: string, d: number) => {
  const [j, mm] = m.split('-').map(Number);
  const x = new Date(j, (mm || 1) - 1 + d, 1);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
};
const ersterTag = (m: string) => `${m}-01`;
const letzterTag = (m: string) => {
  const [j, mm] = m.split('-').map(Number);
  return `${m}-${String(new Date(j, mm, 0).getDate()).padStart(2, '0')}`;
};

const MonatsabschlussSeite: React.FC<Props> = ({ userProfile }) => {
  const { notify } = useIslandActions();
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const [auszahlFuer, setAuszahlFuer] = useState<Zeile | null>(null);
  const darfVerwalten = darfPersonalVerwalten(userProfile);
  const [monat, setMonat] = useState(() => verschieben(new Date().toISOString().slice(0, 7), -1));
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [offeneWarnungen, setOffeneWarnungen] = useState<string | null>(null);
  const [lohnarten, setLohnarten] = useState<Lohnart[]>([]);
  const [personalnummern, setPersonalnummern] = useState<Record<string, string>>({});
  const [datev, setDatev] = useState<{ beraternummer: string | null; mandantennummer: string | null }>({ beraternummer: null, mandantennummer: null });
  const [lohnartenOffen, setLohnartenOffen] = useState(false);
  // Stundensätze je Person — nur Personal/GF bekommt hier überhaupt Zeilen zurück.
  const [verguetung, setVerguetung] = useState<Record<string, number>>({});

  const laden = useCallback(async () => {
    if (!darfVerwalten) { setLaedt(false); return; }
    setLaedt(true);
    const [leute, modelle, abschluesse, ticketZeiten] = await Promise.all([
      ladeMitarbeiter(), ladeModelle(), ladeAbschluesse(monat),
      ladeTicketZeitenZeitraum(ersterTag(monat), letzterTag(monat)),
    ]);
    const saetze = await ladeVerguetung();
    setVerguetung(saetze);
    const neu: Zeile[] = [];
    for (const p of leute) {
      const [stempel, zuordnungen, abwesenheiten, alleStempel, aus] = await Promise.all([
        ladeStempel(p.id, ersterTag(monat), letzterTag(monat)),
        ladeZuordnungen(p.id),
        ladeAbwesenheiten(p.id),
        ladeStempel(p.id, '2020-01-01', tagVon(new Date().toISOString())),
        ladeAuszahlungen(p.id),
      ]) as [Stempel[], Zuordnung[], Abwesenheit[], Stempel[], Auszahlung[]];
      const bilanz = monatsbilanz(monat, stempel, zuordnungen, modelle as Arbeitszeitmodell[], new Date(), abwesenheiten);
      const zuschlag = zuschlaegeSumme(stempel);
      const soll = (t: string) => sollMinutenAmTag(t, zuordnungen, modelle as Arbeitszeitmodell[]);
      const imMonat = abwesenheiten.filter((a) => a.status === 'genehmigt' && a.von_tag <= letzterTag(monat) && a.bis_tag >= ersterTag(monat));
      neu.push({
        id: p.id,
        name: p.full_name || p.email || 'Unbekannt',
        ist: bilanz.istMinuten,
        brutto: bruttoVorschau(bilanz.istMinuten, saetze[p.id]),
        soll: bilanz.sollMinuten,
        saldo: bilanz.saldoMinuten,
        nacht: zuschlag.nacht,
        sonntag: zuschlag.sonntag,
        feiertag: zuschlag.feiertag,
        urlaubstage: imMonat.filter((a) => TYP_INFO[a.typ].urlaubstage).reduce((s, a) => s + urlaubstageImAntrag(a, soll), 0),
        kranktage: imMonat.filter((a) => a.typ === 'krank').reduce((s, a) => s + urlaubstageImAntrag(a, soll), 0),
        verstoesse: pruefeArbeitszeitgesetz(stempel),
        abschluss: abschluesse.find((a) => a.mitarbeiter_id === p.id) || null,
        konto: gesamtsaldo(
          alleStempel, zuordnungen, modelle as Arbeitszeitmodell[], tagVon(new Date().toISOString()),
          new Date(), abwesenheiten, aus.map((x) => ({ datum: x.datum, minuten: x.minuten })),
        ),
        auszahlungen: aus,
        sollTage: bilanz.zeilen.filter((zl) => zl.sollMinuten > 0).length,
        abrechenbar: aufteilung(ticketZeiten.filter((t) => t.mitarbeiter_id === p.id)).abrechenbar,
      });
    }
    setZeilen(neu);
    setLohnarten(await ladeLohnarten());
    setPersonalnummern(await ladePersonalnummern());
    setDatev(await ladeDatevEinstellungen());
    setLaedt(false);
  }, [monat, darfVerwalten]);

  useEffect(() => { void laden(); }, [laden]);

  const abschliessen = async (z: Zeile) => {
    if (z.verstoesse.length > 0 && !await bestaetige({
      nachricht: `Für ${z.name} gibt es ${z.verstoesse.length} Hinweis(e) zum Arbeitszeitgesetz. Trotzdem abschließen?`,
      label: 'Trotzdem abschließen', icon: Warning,
    })) return;
    setArbeitet(z.id);
    const fehler = await monatAbschliessen({
      mitarbeiterId: z.id, monat, istMinuten: z.ist, sollMinuten: z.soll, saldoMinuten: z.saldo,
      nachtMinuten: z.nacht, sonntagMinuten: z.sonntag, feiertagMinuten: z.feiertag,
      urlaubstage: z.urlaubstage, kranktage: z.kranktage, abgeschlossenVon: userProfile?.id || '',
    });
    setArbeitet(null);
    if (fehler) { notify({ title: `Abschluss fehlgeschlagen: ${fehler}`, tone: 'error' }); return; }
    notify({ title: `${monatText(monat)} für ${z.name} abgeschlossen`, tone: 'success' });
    void laden();
  };

  const aufheben = async (z: Zeile) => {
    if (!z.abschluss) return;
    if (!await bestaetige({
      nachricht: `Abschluss für ${z.name} aufheben? Danach sind Zeiten in ${monatText(monat)} wieder änderbar.`,
      label: 'Aufheben', icon: LockSimpleOpen,
    })) return;
    setArbeitet(z.id);
    await abschlussAufheben(z.abschluss.id);
    setArbeitet(null);
    void laden();
  };

  /** Überstunden auszahlen: die Stunden verlassen das Konto zum gewählten Datum. Eingabe und Rückfrage im AuszahlDialog. */
  const auszahlen = (z: Zeile) => {
    if (z.konto <= 0) { notify({ title: `${z.name} hat kein Guthaben`, tone: 'error' }); return; }
    setAuszahlFuer(z);
  };

  const auszahlungAusfuehren = async (z: Zeile, minuten: number) => {
    setArbeitet(z.id);
    const fehler = await auszahlungErfassen({
      mitarbeiterId: z.id, minuten, datum: letzterTag(monat),
      bemerkung: `Auszahlung ${monatText(monat)}`, vonId: userProfile?.id || '',
    });
    setArbeitet(null);
    setAuszahlFuer(null);
    if (fehler) { notify({ title: `Auszahlung fehlgeschlagen: ${fehler}`, tone: 'error' }); return; }
    notify({ title: `${stundenText(minuten)} ausgezahlt`, tone: 'success' });
    void laden();
  };

  /** DATEV Lodas — Bewegungsdaten je Person und Lohnart. */
  const datevExport = () => {
    const ergebnis = baueLodasExport({
      monat,
      lohnarten,
      beraternummer: datev.beraternummer,
      mandantennummer: datev.mandantennummer,
      zeilen: zeilen.map((z) => ({
        personalnummer: personalnummern[z.id] || null,
        name: z.name,
        werte: {
          grundstunden: dezimalStunden(Math.min(z.ist, z.soll)),
          ueberstunden: dezimalStunden(Math.max(0, z.ist - z.soll)),
          nacht: dezimalStunden(z.nacht),
          sonntag: dezimalStunden(z.sonntag),
          feiertag: dezimalStunden(z.feiertag),
          urlaub: z.urlaubstage,
          krank: z.kranktage,
          auszahlung: dezimalStunden(z.auszahlungen.filter((a) => a.datum.startsWith(monat)).reduce((s2, a) => s2 + a.minuten, 0)),
        },
      })),
    });

    if (ergebnis.zeilen === 0) {
      notify({ title: 'Nichts zu exportieren — bitte Lohnarten-Nummern und Personalnummern prüfen', tone: 'error' });
      setLohnartenOffen(true);
      return;
    }
    const blob = new Blob([ergebnis.inhalt], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `lodas-${monat}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);

    const hinweise = [
      ergebnis.ohneNummer.length ? `${ergebnis.ohneNummer.length} Lohnart(en) ohne Nummer` : '',
      ergebnis.ohnePersonalnummer.length ? `${ergebnis.ohnePersonalnummer.length} ohne Personalnummer` : '',
    ].filter(Boolean);
    notify({
      title: hinweise.length
        ? `${ergebnis.zeilen} Zeilen — nicht dabei: ${hinweise.join(', ')}`
        : `${ergebnis.zeilen} Zeilen exportiert`,
      tone: hinweise.length ? 'info' : 'success',
    });
  };

  const exportieren = () => {
    const kopf = csvZeile([
      'Mitarbeiter', 'Monat', 'Ist-Stunden', 'Soll-Stunden', 'Saldo-Stunden',
      'Nacht-Stunden', 'Sonntag-Stunden', 'Feiertag-Stunden', 'Urlaubstage', 'Kranktage',
      'Ausgezahlt-Stunden', 'Stundenkonto', 'Krankheitsquote-%', 'Auslastung-%', 'Abgeschlossen',
    ]);
    const std = (m: number) => Number((m / 60).toFixed(2));
    const zeilenText = zeilen.map((z) => csvZeile([
      z.name, monat, std(z.ist), std(z.soll), std(z.saldo),
      std(z.nacht), std(z.sonntag), std(z.feiertag), z.urlaubstage, z.kranktage,
      std(z.auszahlungen.filter((a) => a.datum.startsWith(monat)).reduce((s2, a) => s2 + a.minuten, 0)),
      std(z.konto),
      krankheitsquote(z.kranktage, z.sollTage) ?? '',
      auslastung(z.abrechenbar, z.ist) ?? '',
      z.abschluss ? 'ja' : 'nein',
    ]));
    // BOM voran, sonst zeigt Excel Umlaute falsch an.
    const blob = new Blob(['﻿' + [kopf, ...zeilenText].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `zeiten-${monat}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  usePillenSteuerung({
    titel: 'Monatsabschluss',
    eintraege: [
      { id: 'monat', typ: 'schritt', farbe: 'indigo', icon: CalendarBlank, kicker: 'Monat', titel: monatText(monat), voll: true,
        onZurueck: () => setMonat(verschieben(monat, -1)), onVor: () => setMonat(verschieben(monat, 1)),
        zurueckLabel: 'Vorheriger Monat', vorLabel: 'Nächster Monat' },
      { id: 'datev', typ: 'aktion', farbe: 'gold', icon: DownloadSimple, kicker: 'Export', titel: 'DATEV / Lodas', unter: 'Lohnexport des Monats',
        onClick: () => { void datevExport(); } },
      { id: 'csv', typ: 'aktion', farbe: 'gruen', icon: FileCsv, kicker: 'Export', titel: 'Übersicht als CSV', unter: `zeiten-${monat}.csv`,
        onClick: exportieren },
      { id: 'lohnarten', typ: 'schalter', farbe: 'grau', icon: ListBullets, kicker: 'Stammdaten', titel: 'Lohnarten pflegen', an: lohnartenOffen,
        onChange: setLohnartenOffen, unter: lohnartenOffen ? 'Bereich eingeblendet' : 'Bereich ausgeblendet', voll: true },
    ],
  }, [monat, zeilen.length, lohnarten, personalnummern, datev, lohnartenOffen]);

  // Vor jedem frühen Return — Hooks müssen in jeder Darstellung gleich oft laufen.
  const summe = useMemo(() => {
    const ist = zeilen.reduce((s, z) => s + z.ist, 0);
    return {
      ist,
      saldo: zeilen.reduce((s, z) => s + z.saldo, 0),
      warnungen: zeilen.reduce((s, z) => s + z.verstoesse.length, 0),
      offen: zeilen.filter((z) => !z.abschluss).length,
      krankheit: krankheitsquote(
        zeilen.reduce((s, z) => s + z.kranktage, 0),
        zeilen.reduce((s, z) => s + z.sollTage, 0),
      ),
      auslastung: auslastung(zeilen.reduce((s, z) => s + z.abrechenbar, 0), ist),
    };
  }, [zeilen]);

  if (!darfVerwalten) {
    return (
      <div className="flex flex-col" style={{ gap: 14 }}>
        <PersonalKopf bereich="monatsabschluss" verwalten={darfVerwalten} mobil={mobil} />
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: 40, gap: 10 }}>
          <span className="flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 18, background: 'var(--fill2)' }}>
            <LockKey size={26} weight="fill" color="var(--ink3)" />
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink2)' }}>Diese Auswertung ist der Personalabteilung vorbehalten.</span>
        </div>
        <GlasTooltipSchicht />
      </div>
    );
  }

  const prozent = (w: number | null) => (w === null ? '—' : `${w.toLocaleString('de-DE')} %`);

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PersonalKopf bereich="monatsabschluss" verwalten={darfVerwalten} mobil={mobil} />

      {/* Steuerzeile — dieselben Handler wie die Pille */}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <MonatSchritt text={monatText(monat)} mobil={mobil} onZurueck={() => setMonat(verschieben(monat, -1))} onVor={() => setMonat(verschieben(monat, 1))} />
        <div className="flex flex-wrap" style={{ gap: 8, marginLeft: mobil ? 0 : 'auto' }}>
          <SteuerKnopf icon={ListNumbers} onClick={() => setLohnartenOffen(!lohnartenOffen)}
            tip={lohnartenOffen ? 'Lohnarten pflegen – Bereich ausblenden' : 'Lohnarten pflegen – Nummern für den DATEV-Export eintragen'}>
            Lohnarten pflegen
          </SteuerKnopf>
          <SteuerKnopf icon={FileCsv} onClick={exportieren} tip="Übersicht als CSV – Alle Mitarbeitenden mit Stunden, Zuschlägen und Quoten">Übersicht als CSV</SteuerKnopf>
          <SteuerKnopf icon={DownloadSimple} primaer onClick={() => { void datevExport(); }} tip="DATEV / Lodas – Bewegungsdaten je Person und Lohnart für die Lohnbuchhaltung">
            DATEV / Lodas
          </SteuerKnopf>
        </div>
      </div>

      <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' }}>
        <KennzahlWidget label="Geleistet" wert={stundenText(summe.ist)} unter="Ist-Stunden aller" icon={Timer} farbe="var(--acc)"
          tip="Geleistet – Ist-Stunden aller Mitarbeitenden" />
        <KennzahlWidget label="Saldo aller" wert={stundenText(summe.saldo, true)} unter="Monatssalden" icon={Scales}
          farbe={summe.saldo >= 0 ? 'var(--ok)' : 'var(--warn)'} wertFarbe={summe.saldo >= 0 ? 'var(--ok)' : 'var(--warn)'}
          tip="Saldo aller – Summe der Monatssalden" />
        <KennzahlWidget label="Hinweise ArbZG" wert={String(summe.warnungen)} unter="mögliche Verstöße" icon={Warning} farbe="var(--warn)"
          wertFarbe={summe.warnungen > 0 ? 'var(--warn)' : undefined} tip="Hinweise ArbZG – Mögliche Verstöße gegen das Arbeitszeitgesetz" />
        <KennzahlWidget label="Krankheitsquote" wert={prozent(summe.krankheit)} unter="Kranktage je Soll-Tag" icon={FirstAid} farbe="var(--bad)"
          wertFarbe={(summe.krankheit ?? 0) > 5 ? 'var(--warn)' : undefined} tip="Krankheitsquote – Kranktage im Verhältnis zu Soll-Arbeitstagen" />
        <KennzahlWidget label="Auslastung" wert={prozent(summe.auslastung)} unter="abrechenbar zu Ist" icon={ChartPieSlice} farbe="var(--info)"
          tip="Auslastung – Abrechenbare Ticketzeit im Verhältnis zu den Ist-Stunden" />
        <KennzahlWidget label="Noch offen" wert={String(summe.offen)} unter="ohne Abschluss" icon={LockSimpleOpen} farbe="var(--pur)"
          tip="Noch offen – Mitarbeitende ohne Abschluss in diesem Monat" />
      </div>

      {lohnartenOffen && (
        <LohnartenWidget lohnarten={lohnarten} datev={datev} onSchliessen={() => setLohnartenOffen(false)}
          onDatev={async (k, wert) => {
            const neu = { ...datev, [k]: wert };
            await setzeDatevEinstellungen(neu.beraternummer || '', neu.mandantennummer || '');
            setDatev(neu);
          }}
          onNummer={(la, nummer) => void lohnartSpeichern(la.schluessel, nummer, la.aktiv).then(() => void laden())}
          onAktiv={(la, aktiv) => void lohnartSpeichern(la.schluessel, la.nummer || '', aktiv).then(() => void laden())} />
      )}

      {laedt ? (
        <div className="flex justify-center" style={{ padding: 48, color: 'var(--ink3)' }}><CircleNotch size={22} className="animate-spin" /></div>
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {zeilen.map((z) => (
            <MonatKarte key={z.id} z={z} stundensatz={verguetung[z.id]} warnungenOffen={offeneWarnungen === z.id} arbeitet={arbeitet === z.id}
              onWarnungen={() => setOffeneWarnungen(offeneWarnungen === z.id ? null : z.id)}
              onAuszahlen={() => auszahlen(z)} onAbschliessen={() => { void abschliessen(z); }} onAufheben={() => { void aufheben(z); }} />
          ))}
        </div>
      )}

      <p className="m-0 flex items-start" style={{ gap: 8, padding: '0 6px', fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)' }}>
        <LockSimpleOpen size={14} weight="bold" className="shrink-0" style={{ marginTop: 1 }} />
        Ein abgeschlossener Monat ist gesperrt: Zeiten und Abwesenheiten lassen sich darin nicht mehr
        ändern — auch nicht von der Personalabteilung. Für eine Korrektur zuerst den Abschluss aufheben.
      </p>

      {auszahlFuer && (
        <AuszahlDialog name={auszahlFuer.name} konto={auszahlFuer.konto} laeuft={arbeitet === auszahlFuer.id}
          zumText={new Date(`${letzterTag(monat)}T12:00:00`).toLocaleDateString('de-DE')} monatText={monatText(monat)}
          onAbbrechen={() => setAuszahlFuer(null)} onAuszahlen={(minuten) => { void auszahlungAusfuehren(auszahlFuer, minuten); }} />
      )}
      {dialoge}
      <GlasTooltipSchicht />
    </div>
  );
};

export default MonatsabschlussSeite;
